import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PrimarySectionBackdrop } from '../components/PrimarySectionBackdrop';
import { Seo } from '../components/Seo';
import { KONTAKT_FORM_HASH } from '../constants/navigation';
import { SEO } from '../constants/seoCopy';
import { 
  Snowflake, 
  Fire, 
  Wind,
  Wrench,
  ArrowRight,
  Thermometer,
  Drop,
  Fan,
  Gauge
} from '@phosphor-icons/react';

const base = process.env.PUBLIC_URL || '';

const IMAGES = {
  klimatizacija: `${base}/usluge/klimatizacija.png`,
  grejanje: `${base}/usluge/grejanje.png`,
  ventilacija: `${base}/usluge/ventilacija.png`,
  vik: `${base}/usluge/vik.png`,
  hidrantskeInstalacije: `${base}/usluge/hidrantske-instalacije.png`,
  sprinkler: `${base}/usluge/sprinkler-sistemi.png`,
  odimljavanjeGaraza: `${base}/usluge/odimljavanje-garaza.png`,
};

const services = [
  {
    id: 'klimatizacija',
    title: 'Klimatizacija',
    galleryCategory: 'Klimatizacija',
    icon: Snowflake,
    color: 'bg-cool',
    textColor: 'text-cool',
    image: IMAGES.klimatizacija,
    description: 'Profesionalna ugradnja, servis i održavanje klima uređaja svih vodećih brendova za optimalan komfor.',
    features: [
      { icon: Thermometer, text: 'Ugradnja split i multi-split sistema' },
      { icon: Wrench, text: 'Redovan servis i čišćenje filtera' },
      { icon: Drop, text: 'Dopuna rashladnog sredstva' },
      { icon: Gauge, text: 'Energetski efikasna A++ rešenja' },
    ],
  },
  {
    id: 'grejanje',
    title: 'Grejanje',
    galleryCategory: 'Grejanje',
    icon: Fire,
    color: 'bg-heat',
    textColor: 'text-heat',
    image: IMAGES.grejanje,
    description: 'Kompletna rešenja za grejanje stambenih, poslovnih i industrijskih objekata sa fokusom na energetsku efikasnost.',
    features: [
      { icon: Fire, text: 'Toplotne pumpe i inverteri' },
      { icon: Thermometer, text: 'Podno i radijatorsko grejanje' },
      { icon: Gauge, text: 'Kotlovi i peći na gas/struju' },
      { icon: Wrench, text: 'Servis i održavanje sistema' },
    ],
  },
  {
    id: 'ventilacija',
    title: 'Ventilacija',
    galleryCategory: 'Ventilacija',
    icon: Wind,
    color: 'bg-accent',
    textColor: 'text-accent',
    image: IMAGES.ventilacija,
    description: 'Kvalitetni ventilacioni sistemi za zdrav i svež vazduh u vašem životnom i radnom prostoru.',
    features: [
      { icon: Fan, text: 'Mehanička ventilacija prostora' },
      { icon: Wind, text: 'Rekuperatori toplote' },
      { icon: Gauge, text: 'Industrijska ventilacija' },
      { icon: Drop, text: 'Kontrola vlažnosti vazduha' },
    ],
  },
  {
    id: 'vik',
    title: 'VIK (vodovod i kanalizacija)',
    galleryCategory: 'VIK (vodovod i kanalizacija)',
    icon: Wrench,
    color: 'bg-primary',
    textColor: 'text-primary',
    image: IMAGES.vik,
    description: 'Izrada i rekonstrukcija vodovodnih i kanalizacionih instalacija za sve tipove objekata.',
    features: [
      { icon: Drop, text: 'Vodovodne instalacije' },
      { icon: Wrench, text: 'Kanalizacioni razvod' },
      { icon: Gauge, text: 'Ispitivanje sistema' },
      { icon: Thermometer, text: 'Sanitarni priključci' },
    ],
  },
  {
    id: 'hidrantske-instalacije',
    title: 'Hidrantske instalacije',
    galleryCategory: 'Hidrantske instalacije',
    icon: Fire,
    color: 'bg-heat',
    textColor: 'text-heat',
    image: IMAGES.hidrantskeInstalacije,
    description: 'Projektovanje i izvođenje hidrantskih mreža u skladu sa propisima i tehničkim standardima.',
    features: [
      { icon: Fire, text: 'Unutrašnja hidrantska mreža' },
      { icon: Gauge, text: 'Ispitivanje pritiska' },
      { icon: Wrench, text: 'Montaža ventila i opreme' },
      { icon: Thermometer, text: 'Servis i održavanje' },
    ],
  },
  {
    id: 'sprinkler-sistemi',
    title: 'Sprinkler sistemi',
    galleryCategory: 'Sprinkler sistemi',
    icon: Drop,
    color: 'bg-cool',
    textColor: 'text-cool',
    image: IMAGES.sprinkler,
    description: 'Automatski sprinkler sistemi za zaštitu od požara u poslovnim i industrijskim objektima.',
    features: [
      { icon: Drop, text: 'Projektovanje sprinkler mreže' },
      { icon: Gauge, text: 'Pumpe i razvod' },
      { icon: Wrench, text: 'Montaža i puštanje u rad' },
      { icon: Fire, text: 'Periodična kontrola sistema' },
    ],
  },
  {
    id: 'odimljavanje-garaza',
    title: 'Odimljavanje garaža',
    galleryCategory: 'Odimljavanje garaža',
    icon: Wind,
    color: 'bg-accent',
    textColor: 'text-accent',
    image: IMAGES.odimljavanjeGaraza,
    description: 'Sistemi odimljavanja i ventilacije garaža za bezbedan rad i boravak u zatvorenim prostorima.',
    features: [
      { icon: Wind, text: 'Ventilatori i odvod dima' },
      { icon: Fan, text: 'Automatika i senzori' },
      { icon: Gauge, text: 'Balans i regulacija protoka' },
      { icon: Wrench, text: 'Servis i održavanje' },
    ],
  },
];

export default function UslugePage() {
  return (
    <div data-testid="usluge-page" className="pt-20">
      <Seo {...SEO.usluge} />
      {/* Hero */}
      <section className="relative py-24 bg-primary overflow-hidden" data-testid="usluge-header">
        <PrimarySectionBackdrop />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <span className="inline-block text-cool font-bold uppercase tracking-[0.2em] text-sm mb-4">
              Naše usluge
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6">
              HVAC i tehničke instalacije
            </h1>
            <p className="text-white/70 text-lg">
              Kompletne usluge klimatizacije, grejanja, ventilacije, VIK-a i protivpožarnih sistema.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-24 bg-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-32">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                data-testid={`service-detail-${service.id}`}
              >
                {/* Image */}
                <div className={`relative ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-[400px] object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${service.color === 'bg-cool' ? 'from-cool/30' : service.color === 'bg-heat' ? 'from-heat/30' : 'from-accent/30'} to-transparent`} />
                  </div>
                  <div className={`absolute -bottom-6 ${index % 2 === 1 ? '-left-6' : '-right-6'} ${service.color} text-white p-6 rounded-2xl shadow-xl`}>
                    <service.icon size={48} weight="duotone" />
                  </div>
                </div>

                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <span className={`inline-block ${service.textColor} font-bold uppercase tracking-[0.2em] text-sm mb-4`}>
                    {service.id}
                  </span>
                  <h2 className="font-heading text-3xl sm:text-4xl font-black text-primary mb-6">
                    {service.title}
                  </h2>
                  <p className="text-muted text-lg mb-8 leading-relaxed">
                    {service.description}
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    {service.features.map((feat, i) => (
                      <div key={i} className="flex items-center gap-3 bg-surface p-4 rounded-xl">
                        <feat.icon size={24} weight="duotone" className={service.textColor} />
                        <span className="text-primary font-medium">{feat.text}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <Link
                      to={`/projekti/zavrseni?${new URLSearchParams({ category: service.galleryCategory }).toString()}`}
                      className="inline-flex items-center gap-3 rounded-xl border border-primary/15 bg-white/85 px-8 py-4 font-bold text-primary shadow-sm backdrop-blur-sm transition-all duration-300 hover:bg-white hover:shadow-lg"
                      data-testid={`service-projects-cta-${service.id}`}
                    >
                      Projekti
                      <ArrowRight size={20} />
                    </Link>
                    <Link
                      to={{
                        pathname: '/kontakt',
                        search: `?${new URLSearchParams({
                          service: service.title,
                          message: `Interesuje me usluga: ${service.title}.`,
                        }).toString()}`,
                        hash: KONTAKT_FORM_HASH,
                        state: {
                          prefillService: service.title,
                          prefillMessage: `Interesuje me usluga: ${service.title}.`,
                        },
                      }}
                      className="inline-flex items-center gap-3 rounded-xl border border-primary/15 bg-white/85 px-8 py-4 font-bold text-primary shadow-sm backdrop-blur-sm transition-all duration-300 hover:bg-white hover:shadow-lg"
                      data-testid={`service-cta-${service.id}`}
                    >
                      Kontaktirajte nas
                      <ArrowRight size={20} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden py-20 bg-primary" data-testid="usluge-cta">
        <PrimarySectionBackdrop />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-black text-white mb-6">
              Potrebna vam je naša usluga?
            </h2>
            <p className="text-white/70 text-lg mb-8">
              Kontaktirajte nas za besplatnu konsultaciju i procenu.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to={{
                  pathname: '/kontakt',
                  hash: KONTAKT_FORM_HASH,
                }}
                className="inline-flex items-center gap-3 rounded-xl border border-white/45 bg-white/20 px-8 py-4 font-bold text-white shadow-[0_10px_28px_rgba(255,255,255,0.16)] backdrop-blur-md transition-all duration-300 hover:border-white/70 hover:bg-white/30"
                data-testid="usluge-cta-contact"
              >
                Javite nam se
                <ArrowRight size={24} weight="bold" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
